import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { backendApi } from '../lib/backendApi';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Loader2, Plus, Edit, Trash2, Wallet } from 'lucide-react';
import { toast } from 'sonner';

const blankEmployee = {
  full_name: '',
  position: '',
  phone: '',
  email: '',
  address: '',
  monthly_salary: '',
  start_date: '',
  notes: '',
};

const blankExpense = {
  name: '',
  category: '',
  amount: '',
  amount_paid: '',
  creditor_name: '',
  is_recurring: false,
  recurrence_period: 'monthly',
  due_date: '',
  notes: '',
};

const OperationsPage = () => {
  const { api, isOwner, isManager } = useAuth();
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [expenses, setExpenses] = useState([]);

  const [employeeDialogOpen, setEmployeeDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [employeeForm, setEmployeeForm] = useState(blankEmployee);

  const [expenseForm, setExpenseForm] = useState(blankExpense);
  const [expensePaymentDialogOpen, setExpensePaymentDialogOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [expensePaymentData, setExpensePaymentData] = useState({
    amount: '',
    payment_method: 'cash',
    notes: '',
  });

  const [submitting, setSubmitting] = useState(false);

  const canManageEmployees = isOwner;
  const canManageExpenses = isOwner || isManager;

  const fetchData = useCallback(async () => {
    try {
      const tasks = [];
      if (canManageEmployees) {
        tasks.push(backendApi.employees.list(api).then((r) => ({ key: 'employees', data: r.data || [] })));
      }
      if (canManageExpenses) {
        tasks.push(backendApi.expenses.list(api).then((r) => ({ key: 'expenses', data: r.data || [] })));
      }
      const results = await Promise.all(tasks);
      for (const res of results) {
        if (res.key === 'employees') setEmployees(res.data);
        if (res.key === 'expenses') setExpenses(res.data);
      }
    } catch (error) {
      toast.error('Failed to load operations data');
    } finally {
      setLoading(false);
    }
  }, [api, canManageEmployees, canManageExpenses]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatCurrency = (amount) => `UGX ${Number(amount || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

  const openAddEmployee = () => {
    setEditingEmployee(null);
    setEmployeeForm(blankEmployee);
    setEmployeeDialogOpen(true);
  };

  const openEditEmployee = (employee) => {
    setEditingEmployee(employee);
    setEmployeeForm({
      full_name: employee.full_name || '',
      position: employee.position || '',
      phone: employee.phone || '',
      email: employee.email || '',
      address: employee.address || '',
      monthly_salary: String(employee.monthly_salary ?? ''),
      start_date: employee.start_date ? new Date(employee.start_date).toISOString().slice(0, 16) : '',
      notes: employee.notes || '',
    });
    setEmployeeDialogOpen(true);
  };

  const submitEmployee = async () => {
    if (!employeeForm.full_name || employeeForm.monthly_salary === '') {
      toast.error('Full name and monthly salary are required');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        full_name: employeeForm.full_name,
        position: employeeForm.position || null,
        phone: employeeForm.phone || null,
        email: employeeForm.email || null,
        address: employeeForm.address || null,
        monthly_salary: Number(employeeForm.monthly_salary),
        start_date: employeeForm.start_date ? new Date(employeeForm.start_date).toISOString() : null,
        notes: employeeForm.notes || null,
      };
      if (editingEmployee) {
        await backendApi.employees.update(api, editingEmployee.id, payload);
        toast.success('Employee updated');
      } else {
        await backendApi.employees.create(api, payload);
        toast.success('Employee created');
      }
      setEmployeeDialogOpen(false);
      setEmployeeForm(blankEmployee);
      setEditingEmployee(null);
      await fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to save employee');
    } finally {
      setSubmitting(false);
    }
  };

  const deactivateEmployee = async (employeeId) => {
    if (!window.confirm('Deactivate this employee?')) return;
    try {
      await backendApi.employees.remove(api, employeeId);
      toast.success('Employee deactivated');
      await fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to deactivate employee');
    }
  };

  const submitExpense = async () => {
    if (!expenseForm.name || !expenseForm.category || !expenseForm.amount) {
      toast.error('Name, category, and amount are required');
      return;
    }
    const amount = Number(expenseForm.amount);
    const amountPaid = Number(expenseForm.amount_paid || 0);
    if (amountPaid < amount && !expenseForm.creditor_name) {
      toast.error('Creditor name is required for partial/credit expense');
      return;
    }
    setSubmitting(true);
    try {
      await backendApi.expenses.create(api, {
        name: expenseForm.name,
        category: expenseForm.category,
        amount,
        amount_paid: amountPaid,
        creditor_name: expenseForm.creditor_name || null,
        is_recurring: Boolean(expenseForm.is_recurring),
        recurrence_period: expenseForm.recurrence_period || 'monthly',
        due_date: expenseForm.due_date ? new Date(expenseForm.due_date).toISOString() : null,
        notes: expenseForm.notes || null,
      });
      toast.success('Expense recorded');
      setExpenseForm(blankExpense);
      await fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to create expense');
    } finally {
      setSubmitting(false);
    }
  };

  const openExpensePayment = (expense) => {
    setSelectedExpense(expense);
    setExpensePaymentData({
      amount: String(expense.outstanding_amount || ''),
      payment_method: 'cash',
      notes: '',
    });
    setExpensePaymentDialogOpen(true);
  };

  const submitExpensePayment = async () => {
    if (!selectedExpense || !expensePaymentData.amount) {
      toast.error('Enter payment amount');
      return;
    }
    setSubmitting(true);
    try {
      await backendApi.expenses.addPayment(api, selectedExpense.id, {
        amount: Number(expensePaymentData.amount),
        payment_method: expensePaymentData.payment_method,
        notes: expensePaymentData.notes || null,
      });
      toast.success('Expense payment recorded');
      setExpensePaymentDialogOpen(false);
      setSelectedExpense(null);
      await fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to record expense payment');
    } finally {
      setSubmitting(false);
    }
  };

  const outstandingExpenses = useMemo(() => expenses.filter((e) => Number(e.outstanding_amount || 0) > 0), [expenses]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="operations-page">
      <div>
        <h1 className="font-heading text-3xl font-bold">Operations</h1>
        <p className="text-muted-foreground">Manage employees and operating expenses.</p>
      </div>

      <Tabs defaultValue="employees">
        <TabsList>
          <TabsTrigger value="employees">Employees</TabsTrigger>
          <TabsTrigger value="expenses">Expenses</TabsTrigger>
        </TabsList>

        <TabsContent value="employees" className="mt-4">
          {!canManageEmployees ? (
            <Card><CardHeader><CardDescription>Only owners can manage employees.</CardDescription></CardHeader></Card>
          ) : (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Employees</CardTitle>
                  <CardDescription>Create and maintain staff records.</CardDescription>
                </div>
                <Button onClick={openAddEmployee}><Plus className="h-4 w-4 mr-2" />Add Employee</Button>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Position</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Salary</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {employees.map((employee) => (
                      <TableRow key={employee.id}>
                        <TableCell className="font-medium">{employee.full_name}</TableCell>
                        <TableCell>{employee.position || '-'}</TableCell>
                        <TableCell>{employee.phone || '-'}</TableCell>
                        <TableCell className="font-mono">{formatCurrency(employee.monthly_salary)}</TableCell>
                        <TableCell>
                          <Badge variant={employee.is_active ? 'default' : 'secondary'}>
                            {employee.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right space-x-2">
                          <Button variant="ghost" size="sm" onClick={() => openEditEmployee(employee)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          {employee.is_active ? (
                            <Button variant="ghost" size="sm" onClick={() => deactivateEmployee(employee.id)}>
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    ))}
                    {!employees.length ? (
                      <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No employees yet.</TableCell></TableRow>
                    ) : null}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="expenses" className="mt-4">
          {!canManageExpenses ? (
            <Card><CardHeader><CardDescription>You do not have access to expenses.</CardDescription></CardHeader></Card>
          ) : (
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Record Expense</CardTitle>
                  <CardDescription>Create a payable/paid expense entry.</CardDescription>
                </CardHeader>
                <CardContent className="grid md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Name</Label>
                    <Input value={expenseForm.name} onChange={(e) => setExpenseForm((p) => ({ ...p, name: e.target.value }))} />
                  </div>
                  <div className="space-y-2">
                    <Label>Category</Label>
                    <Input value={expenseForm.category} onChange={(e) => setExpenseForm((p) => ({ ...p, category: e.target.value }))} />
                  </div>
                  <div className="space-y-2">
                    <Label>Amount</Label>
                    <Input type="number" value={expenseForm.amount} onChange={(e) => setExpenseForm((p) => ({ ...p, amount: e.target.value }))} />
                  </div>
                  <div className="space-y-2">
                    <Label>Amount Paid</Label>
                    <Input type="number" value={expenseForm.amount_paid} onChange={(e) => setExpenseForm((p) => ({ ...p, amount_paid: e.target.value }))} />
                  </div>
                  <div className="space-y-2">
                    <Label>Creditor Name</Label>
                    <Input value={expenseForm.creditor_name} onChange={(e) => setExpenseForm((p) => ({ ...p, creditor_name: e.target.value }))} />
                  </div>
                  <div className="space-y-2">
                    <Label>Recurring</Label>
                    <Select
                      value={expenseForm.is_recurring ? 'true' : 'false'}
                      onValueChange={(v) => setExpenseForm((p) => ({ ...p, is_recurring: v === 'true' }))}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="false">No</SelectItem>
                        <SelectItem value="true">Yes</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Recurrence Period</Label>
                    <Select
                      value={expenseForm.recurrence_period}
                      onValueChange={(v) => setExpenseForm((p) => ({ ...p, recurrence_period: v }))}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="yearly">Yearly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Due Date</Label>
                    <Input type="datetime-local" value={expenseForm.due_date} onChange={(e) => setExpenseForm((p) => ({ ...p, due_date: e.target.value }))} />
                  </div>
                  <div className="space-y-2 md:col-span-3">
                    <Label>Notes</Label>
                    <Textarea value={expenseForm.notes} onChange={(e) => setExpenseForm((p) => ({ ...p, notes: e.target.value }))} rows={3} />
                  </div>
                  <div className="md:col-span-3 flex justify-end">
                    <Button onClick={submitExpense} disabled={submitting}>
                      {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
                      Add Expense
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Expenses</CardTitle>
                  <CardDescription>Track outstanding and recorded payments.</CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Total</TableHead>
                        <TableHead>Paid</TableHead>
                        <TableHead>Outstanding</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {expenses.map((expense) => (
                        <TableRow key={expense.id}>
                          <TableCell className="font-medium">{expense.name}</TableCell>
                          <TableCell>{expense.category}</TableCell>
                          <TableCell className="font-mono">{formatCurrency(expense.amount)}</TableCell>
                          <TableCell className="font-mono text-primary">{formatCurrency(expense.amount_paid)}</TableCell>
                          <TableCell className="font-mono text-destructive">{formatCurrency(expense.outstanding_amount)}</TableCell>
                          <TableCell><Badge variant="outline">{expense.payment_status}</Badge></TableCell>
                          <TableCell className="text-right">
                            {Number(expense.outstanding_amount) > 0 ? (
                              <Button variant="outline" size="sm" onClick={() => openExpensePayment(expense)}>
                                <Wallet className="h-4 w-4 mr-2" />Pay
                              </Button>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      ))}
                      {!expenses.length ? (
                        <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No expenses yet.</TableCell></TableRow>
                      ) : null}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {!!outstandingExpenses.length && (
                <Alert>
                  <AlertDescription>
                    Outstanding liabilities: {formatCurrency(outstandingExpenses.reduce((s, e) => s + Number(e.outstanding_amount || 0), 0))}
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={employeeDialogOpen} onOpenChange={setEmployeeDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingEmployee ? 'Edit Employee' : 'Add Employee'}</DialogTitle>
            <DialogDescription>Maintain staff records used in operations and reports.</DialogDescription>
          </DialogHeader>
          <div className="grid md:grid-cols-2 gap-4 py-2">
            <div className="space-y-2 md:col-span-2">
              <Label>Full Name</Label>
              <Input value={employeeForm.full_name} onChange={(e) => setEmployeeForm((p) => ({ ...p, full_name: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Position</Label>
              <Input value={employeeForm.position} onChange={(e) => setEmployeeForm((p) => ({ ...p, position: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Monthly Salary</Label>
              <Input type="number" value={employeeForm.monthly_salary} onChange={(e) => setEmployeeForm((p) => ({ ...p, monthly_salary: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input value={employeeForm.phone} onChange={(e) => setEmployeeForm((p) => ({ ...p, phone: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" value={employeeForm.email} onChange={(e) => setEmployeeForm((p) => ({ ...p, email: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Start Date</Label>
              <Input type="datetime-local" value={employeeForm.start_date} onChange={(e) => setEmployeeForm((p) => ({ ...p, start_date: e.target.value }))} />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Address</Label>
              <Input value={employeeForm.address} onChange={(e) => setEmployeeForm((p) => ({ ...p, address: e.target.value }))} />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Notes</Label>
              <Textarea value={employeeForm.notes} onChange={(e) => setEmployeeForm((p) => ({ ...p, notes: e.target.value }))} rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmployeeDialogOpen(false)}>Cancel</Button>
            <Button onClick={submitEmployee} disabled={submitting}>
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              {editingEmployee ? 'Update Employee' : 'Create Employee'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={expensePaymentDialogOpen} onOpenChange={setExpensePaymentDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Record Expense Payment</DialogTitle>
            <DialogDescription>
              {selectedExpense ? (
                <>Outstanding for <strong>{selectedExpense.name}</strong>: {formatCurrency(selectedExpense.outstanding_amount)}</>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Amount</Label>
              <Input type="number" value={expensePaymentData.amount} onChange={(e) => setExpensePaymentData((p) => ({ ...p, amount: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Payment Method</Label>
              <Select
                value={expensePaymentData.payment_method}
                onValueChange={(v) => setExpensePaymentData((p) => ({ ...p, payment_method: v }))}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="mtn_mobile_money">MTN MoMo</SelectItem>
                  <SelectItem value="airtel_money">Airtel Money</SelectItem>
                  <SelectItem value="bank_card">Bank Card</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={expensePaymentData.notes} onChange={(e) => setExpensePaymentData((p) => ({ ...p, notes: e.target.value }))} rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExpensePaymentDialogOpen(false)}>Cancel</Button>
            <Button onClick={submitExpensePayment} disabled={submitting}>
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Wallet className="h-4 w-4 mr-2" />}
              Record Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OperationsPage;
